import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  Button,
  Badge,
  Icon,
  Spinner,
  Center,
  Checkbox,
} from '@chakra-ui/react'
import { SearchIcon } from '@chakra-ui/icons'
import { LuUser, LuUserPlus } from 'react-icons/lu'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { usePatientsList } from '../../../../api/api_hooks/patients'
import { useTranslation } from 'react-i18next'
import { useTranslatedQueryHook } from '../../../../utils/useTranslatedQueryHook'
import PatientForm from '../../../Forms/patientForm'

interface PatientSelectionStepProps {
  onPatientSelect?: (patient: any) => void
}

export default function PatientSelectionStep({ onPatientSelect }: PatientSelectionStepProps) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewPatientForm, setShowNewPatientForm] = useState(false)
  const { setValue, watch } = useFormContext()
  const selectedPatientId = watch('patientId')

  const { data: patientsData, isLoading, refetch } = useTranslatedQueryHook(
    usePatientsList()
  )

  const patients = patientsData?.data || []

  const filteredPatients = patients.filter((patient: any) => {
    const searchLower = searchQuery.toLowerCase()
    const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase()
    const birthDate = patient.dateOfBirth || ''
    return (
      fullName.includes(searchLower) ||
      birthDate.includes(searchLower) ||
      patient.id?.toString().includes(searchLower)
    )
  })

  const handleSelectPatient = (patient: any) => {
    console.log('🔍 PatientSelectionStep - Selecting patient:', patient)
    console.log('🔍 PatientSelectionStep - Patient ID:', patient?.id)
    const normalizedId = Number(patient.id)
    const patientIdValue = Number.isNaN(normalizedId) ? patient.id : normalizedId
    setValue('patientId', patientIdValue, { shouldValidate: true })
    console.log('🔍 PatientSelectionStep - After setValue, checking form value:', watch('patientId'))
    if (onPatientSelect) {
      onPatientSelect(patient)
    }
  }

  const handlePatientCreated = (newPatient: any) => {
    console.log('✨ PatientSelectionStep - Patient created callback received:', newPatient)
    console.log('✨ PatientSelectionStep - Has ID?:', newPatient?.id)
    refetch()
    setShowNewPatientForm(false)
    if (newPatient && newPatient.id) {
      console.log('✨ PatientSelectionStep - Calling handleSelectPatient with:', newPatient)
      handleSelectPatient(newPatient)
    } else {
      console.error('❌ PatientSelectionStep - No patient ID found in:', newPatient)
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="purple.500" thickness="4px" />
          <Text color="gray.600">{t('shared.common.loading')}</Text>
        </VStack>
      </Center>
    )
  }

  if (showNewPatientForm) {
    return (
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
              {t('patient.newPatient')}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {t('patient.newPatientInfo')}
            </Text>
          </Box>
          <Button
            variant="ghost"
            onClick={() => setShowNewPatientForm(false)}
          >
            {t('shared.common.cancel')}
          </Button>
        </HStack>
        <PatientForm onClose={handlePatientCreated} noContainer={true} />
      </VStack>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
          {t('sharedDiagnostic.patientSelection.title') || 'Sélectionner un patient'}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {t('sharedDiagnostic.patientSelection.description') ||
            'Choisissez le patient concerné par cette demande de téléexpertise'}
        </Text>
      </Box>

      <HStack spacing={3}>
        <InputGroup flex={1}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={
              t('sharedDiagnostic.patientSelection.searchPlaceholder') ||
              'Rechercher par nom, prénom ou date de naissance...'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="white"
            borderColor="gray.300"
            _focus={{
              borderColor: 'purple.400',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)',
            }}
          />
        </InputGroup>
        <Button
          leftIcon={<Icon as={LuUserPlus} />}
          colorScheme="green"
          onClick={() => setShowNewPatientForm(true)}
          flexShrink={0}
        >
          {t('patient.newPatient')}
        </Button>
      </HStack>

      <Box maxH="400px" overflowY="auto" pr={2}>
        {filteredPatients.length === 0 ? (
          <Center py={10}>
            <VStack spacing={3}>
              <Icon as={LuUser} boxSize={12} color="gray.400" />
              <Text fontSize="md" color="gray.500" fontWeight="medium">
                {searchQuery
                  ? t('sharedDiagnostic.patientSelection.noResults') ||
                    'Aucun patient trouvé'
                  : t('sharedDiagnostic.patientSelection.noPatients') ||
                    'Aucun patient enregistré'}
              </Text>
              <Button
                leftIcon={<Icon as={LuUserPlus} />}
                colorScheme="purple"
                size="sm"
                onClick={() => setShowNewPatientForm(true)}
              >
                {t('sharedDiagnostic.patientSelection.createFirst') ||
                  'Créer votre premier patient'}
              </Button>
            </VStack>
          </Center>
        ) : (
          <VStack spacing={3} align="stretch">
            {filteredPatients.map((patient: any) => {
              const isSelected =
                selectedPatientId !== undefined &&
                selectedPatientId !== null &&
                String(selectedPatientId) === String(patient.id)
              return (
                <Card
                  key={patient.id}
                  variant={isSelected ? 'filled' : 'outline'}
                  bg={isSelected ? 'purple.50' : 'white'}
                  borderColor={isSelected ? 'purple.500' : 'gray.200'}
                  borderWidth={isSelected ? '2px' : '1px'}
                  cursor="pointer"
                  onClick={() => handleSelectPatient(patient)}
                  _hover={{
                    borderColor: 'purple.400',
                    boxShadow: 'sm',
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.2s"
                >
                  <CardBody p={4}>
                    <HStack spacing={4} justify="space-between">
                      <HStack spacing={3} flex={1}>
                        <Box
                          p={2}
                          borderRadius="full"
                          bg={isSelected ? 'purple.100' : 'gray.100'}
                        >
                          <Icon
                            as={LuUser}
                            boxSize={5}
                            color={isSelected ? 'purple.600' : 'gray.600'}
                          />
                        </Box>
                        <VStack align="start" spacing={0} flex={1}>
                          <HStack>
                            <Text fontWeight="bold" fontSize="md" color="gray.800">
                              {patient.firstName} {patient.lastName}
                            </Text>
                            {isSelected && (
                              <Badge colorScheme="purple" fontSize="xs">
                                {t('sharedDiagnostic.patientSelection.selected') ||
                                  'Sélectionné'}
                              </Badge>
                            )}
                          </HStack>
                          <HStack spacing={4} fontSize="sm" color="gray.600">
                            {patient.dateOfBirth && (
                              <Text>
                                {t('shared.common.on')}{' '}
                                {new Date(patient.dateOfBirth).toLocaleDateString(
                                  'fr-FR'
                                )}
                              </Text>
                            )}
                            {patient.gender && (
                              <Text>
                                {patient.gender === 'MALE'
                                  ? t('shared.common.male')
                                  : t('shared.common.female')}
                              </Text>
                            )}
                          </HStack>
                        </VStack>
                      </HStack>
                      <Checkbox
                        isChecked={isSelected}
                        pointerEvents="none"
                        colorScheme="purple"
                        size="lg"
                        borderColor={isSelected ? 'purple.500' : 'gray.300'}
                        bg="white"
                      />
                    </HStack>
                  </CardBody>
                </Card>
              )
            })}
          </VStack>
        )}
      </Box>

      {!selectedPatientId && (
        <Box
          p={3}
          bg="orange.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="orange.400"
        >
          <Text fontSize="sm" color="orange.800">
            <strong>⚠️ {t('shared.common.optional')}</strong> —{' '}
            {t('sharedDiagnostic.patientSelection.warning') ||
              'Veuillez sélectionner un patient avant de continuer'}
          </Text>
        </Box>
      )}
    </VStack>
  )
}
